(ns dre.pages.dictionary.term-meta
  (:require
   ["antd" :refer [Row Col Divider]]
   [camel-snake-kebab.core :as csk]
   [clojure.set :as set]
   [clojure.string :as str]
   [dre.app.router :refer [->route]]
   [dre.components.list :refer [collapsable-list]]
   [dre.components.table :refer [table]]
   [dre.config :as config]
   [dre.coll :refer [provide-coll provide-vector]]
   [dre.pages.detail.views :as ddv]
   [dre.pages.dictionary.util :as tutil]
   [dre.pages.dictionary.term :as ddt]
   [dre.page-util :refer [enum]]
   [dre.i18n.views :refer [tr]]
   [dre.util :refer [url?]]
   [dre.shared.interface.semgroups :refer [semgroup-label]]
   [reagent.core :as r]))

;; utils 

(def excluded-properties
  #{:term/uri
    :vocab/uri
    :term/tuis
    :term/cui
    :term/sense
    :term/label
    :term/alts})

(def known-properties
  #{:uri :tui :type :vocabulary
    (keyword "http://www.w3.org/2004/02/skos/core#prefLabel")
    (keyword "http://www.w3.org/2004/02/skos/core#altLabel")
    (keyword "http://www.w3.org/2004/02/skos/core#hiddenLabel")
    (keyword "http://bioportal.bioontology.org/ontologies/umls/cui")
    (keyword "http://www.w3.org/2004/02/skos/core#definition")
    (keyword "http://bioportal.bioontology.org/ontologies/umls/hasSTY")
    (keyword "http://bioportal.bioontology.org/ontologies/umls/tui")
    (keyword "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
    (keyword "http://vocab.drevidence.com/property/term#base_sense")
    (keyword "http://vocab.drevidence.com/property/term#sense")
    (keyword "http://vocab.drevidence.com/type")})

(def hidden-properties
  (set/union known-properties excluded-properties))

(defn underscores->spaces
  "Return the result of turning spaces into underscores in input
   string."
  [s]
  (str/replace s #"_" " ")
  )

(defn drugbank->snake_case
  [s]
  (if (str/starts-with? s ":http://vocab.drevidence.com/drugbank#")
    (csk/->snake_case s)
    s))

(defn url->property
  "Return a property name from an input URL string."
  [url-str]
  (-> url-str
      drugbank->snake_case
      tutil/extract-term-from-url
      underscores->spaces
      clojure.string/capitalize))

(defn remove-multiple-drugbank-entities
  [terms]
  (let [multiple-drugbank?
        (fn [[k v]]
          (or (= (count v) 1)
              (not (str/includes? (or k "") "drugbank"))))]
    (->> (group-by #(get % :rdf/type) terms)
         (filter multiple-drugbank?)
         (mapcat val))))

(defn group-term-properties
  [term]
  (let [vocab (tutil/term-vocabulary term)
        kf #(str vocab "/" (url->property (str %)))
        props (remove #(hidden-properties (first %))
                      (seq term))]
    (reduce
     (fn [mem [k v]]
       (let [k* (kf k)]
         (assoc mem k*
                (concat
                 (get mem k* [])
                 (provide-vector v))))) {} props)))

(defn prepare-terms-meta
  [terms]
  (->> (remove-multiple-drugbank-entities terms)
       (map group-term-properties)
       (apply merge-with (comp distinct concat))))

(defn term-senses
  [doc]
  (distinct (mapv #(get % :term/sense) (:terms doc))))

(defn term-link
  [term]
  (let [uri (get term :term/uri)]
    [:a {:href uri
         :target "_blank"}
     uri]))

(defn term-sty
  [term]
  (provide-coll
   (or (get term (keyword "http://bioportal.bioontology.org/ontologies/umls/hasSTY"))
       (map #(str "http://purl.bioontology.org/ontology/STY/" %)
            (provide-coll (get term :term/tuis))))))

(defn term-meanings
  [term]
  (provide-coll (get term :term/cui)))

;; views

(defn sense-row
  [doc admin-role?]
  (when admin-role?
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "DocSearch sense"]]]
      [:> Col
       {:span 20}
       (for [sense (term-senses doc)]
         (with-meta
           [:span {:style
                   {:font-family :monospace}}
            sense [:br]]
           {:key (hash sense)}))]]
     [:> Divider]]))

(defn semgroup-row
  [doc]
  [:<>
   [:> Row
    {:gutter 16}
    [:> Col
     {:span 4}
     [:b [tr "Semantic Group"]]]
    [:> Col
     {:span 20}
     (str/join ", " (distinct (map (fn [v] (get (semgroup-label) (keyword v)))
                                   (:sense/semgroups doc))))]]
   [:> Divider]])

(defn semtype-row
  [{:keys [terms label-map]}]
  (let []
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Semantic Type"]]]
      [:> Col
       {:span 20}
       (for [[idx sty] (enum (->> terms
                                  (mapcat term-sty)
                                  (distinct)
                                  (map #(get label-map (keyword %) %))))]
         ^{:key idx}
         [:span
          (if (str/starts-with? sty "http")
            [:a {:href sty
                 :rel "noopener noreferrer"
                 :target "_blank"} sty]
            sty)
          [:br]])]]
     [:> Divider]]))

(defn umls-cui-row
  [{:keys [terms]}]
  [:<>
   [:> Row
    {:gutter 16}
    [:> Col
     {:span 4}
     [:b [tr "UMLS CUI"]]]
    [:> Col
     {:span 20}
     (str/join ", " (distinct (mapcat term-meanings terms)))]]
   [:> Divider]])

(defn uri-row
  [doc admin-role?]
  (when admin-role?
    [:<>
     [:> Row
      {:gutter 16}
      [:> Col
       {:span 4}
       [:b [tr "Uniform Resource Identifiers"]]]
      [:> Col
       {:span 20}
       (doall
        (for [t (distinct (:terms doc))]
          (with-meta
            [:span {:style
                    {:font-family :monospace}}
             [term-link t] [:br]]
            {:key (hash t)})))]]
     [:> Divider]]))
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn meta-row
  [values label-map guid-map]
  (let [values (sort values)
        gf #(let [{:keys [:curie/prefix :curie/valid?]} (ddt/term-curie %)]
              (when valid? prefix))
        groups (group-by gf values)
        single? (= 1 (count groups))]
    (doall
     (for [[prefix values] groups]
       (r/as-element
        ^{:key tutil/random-str}
        [:div
         (when-not single?
           (if prefix
             [:span [:b prefix ":"] [:br]]
             [:span [:b "Other:"] [:br]]))
         [collapsable-list
          (mapv (fn [val]
                  (when-let [l (or (get label-map (keyword val)) val)]
                    {:content (r/as-element [:span
                                             {:style {:word-wrap "anywhere"}}
                                             (cond
                                               (and (string? val) (url? val)) [ddt/link label-map guid-map val]
                                               (and (string? val) (> (count val) 100)) (dre.page-util/md (str l))
                                               :else (str l))
                                             [:br]])})) values)
          3]])))))

(defn drugbank-generic-table
  [values]
  (let [column-names (keys (first values))]
    [table
     {:columns (map (fn [v]
                      {:title v
                       :render (fn [data]
                                 (get data v))}) column-names) 
      :dataSource values
      :pagination {:defaultPageSize 10
                   :hideOnSinglePage true}}]))

(defn drugbank-external-links-table
  [values]
  [table
   {:columns [{:title "Ref"
               :render (fn [data]
                         (get data :ref_id))}
              {:title "Title"
               :render (fn [data]
                         (get data :title))}
              {:title "url"
               :render (fn [data]
                         [:a {:target "_blank" :href (get data :url)} (get data :url)])}]
    :dataSource values
    :pagination {:defaultPageSize 10
                 :hideOnSinglePage true}}])

(defn drugbank-literature-ref-table
  [values]
  [table
   {:columns [{:title "Ref"
               :render (fn [data]
                         [:a.truncate {:target "_blank" :href (->route :detail {:id (get data :guid)})} (get data :ref_id)])}
              {:title "PubMed"
               :render (fn [data]
                         [:a.truncate {:target "_blank" :href (ddv/pubmed-uri (get data :pubmed_id))} (get data :pubmed_id)])}
              {:title "Citation"
               :render (fn [data]
                         (get data :citation))}]
    :dataSource values
    :pagination {:defaultPageSize 10
                 :hideOnSinglePage true}}])

(defn metadata-rows
  [label-map guid-map props]
  (doall
   (for [[k v] props]
     ^{:key k}
     [:<>
      [:> Row
       {:gutter 16}
       [:> Col
        {:span 4}
        (let [[vocab prop] (str/split k #"/")]
          (if (#{"drugbank" "umls"} (str/lower-case vocab))
            [:b prop]
            [:<>
             [:span {:style {:font-weight "lighter"}} vocab]
             [:br]
             [:b prop]]))]
       [:> Col
        {:span 20}
        (case k
          "drugbank/Structure"
          [:div
           (vec (cons :<> (map #(vector :img {:src %
                                              :style
                                              {:max-width "100%"
                                               :max-height "100%"}})
                               (provide-coll v))))]
          "drugbank/Literature references" [drugbank-literature-ref-table v]
          "drugbank/External links" [drugbank-external-links-table v]
          "drugbank/Attachments"  [drugbank-external-links-table v]
          "drugbank/Go classes" [drugbank-generic-table v]
          "drugbank/Pfams" [drugbank-generic-table v]
          "drugbank/Textbooks" [drugbank-generic-table v]
          [meta-row v label-map guid-map])]]
      [:> Divider]])))